/**
 * DialogHost — single rendering surface for the imperative dialog API.
 *
 * Mounted once in AppLayout. Consumes `useDialogStore` and renders either
 * a Confirm or Prompt dialog when there's an active spec. Handles focus,
 * Escape, and backdrop-click affordances.
 *
 * Styling matches the existing PaymentLoggerModal: `bg-surface` panel,
 * `rounded-xl shadow-2xl`, `font-headline` title, Tailwind tokens only.
 */

import { useEffect, useRef, useState } from 'react';
import { cx } from './cx';
import { useDialogStore } from './dialogStore';

// Match Button kit's md size visually. Plain <button> here so we can hold
// a ref for autofocus (Button doesn't forward refs).
const BUTTON_BASE =
  'inline-flex items-center justify-center h-9 px-3.5 text-sm rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const BUTTON_CANCEL =
  'bg-surface border border-outline-variant text-on-surface hover:bg-surface-container-highest';
const BUTTON_CONFIRM_DEFAULT = 'bg-primary text-white hover:opacity-90 shadow-sm';
const BUTTON_CONFIRM_DESTRUCTIVE = 'bg-red-600 text-white hover:bg-red-700 shadow-sm';

export default function DialogHost() {
  const active = useDialogStore((s) => s.active);
  const resolve = useDialogStore((s) => s.resolve);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Reset input + autofocus when a dialog opens.
  useEffect(() => {
    if (!active) return;
    if (active.kind === 'prompt') {
      setInputValue(active.defaultValue ?? '');
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      requestAnimationFrame(() => confirmButtonRef.current?.focus());
    }
  }, [active]);

  // Escape cancels. Window-level so it fires even when focus is on backdrop.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (active.kind === 'confirm') resolve(false);
        else resolve(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, resolve]);

  if (!active) return null;

  const cancel = () => {
    if (active.kind === 'confirm') resolve(false);
    else resolve(null);
  };

  const confirm = () => {
    if (active.kind === 'confirm') {
      resolve(true);
      return;
    }
    const trimmed = inputValue.trim();
    if (active.required && !trimmed) return;
    resolve(trimmed.length > 0 ? trimmed : '');
  };

  const tone = active.tone ?? 'default';
  const confirmLabel = active.confirmLabel ?? (active.kind === 'prompt' ? 'OK' : 'Confirm');
  const cancelLabel = active.cancelLabel ?? 'Cancel';
  const disableConfirm = active.kind === 'prompt' && !!active.required && !inputValue.trim();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="wss-dialog-title"
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={cancel}
    >
      <div
        className="bg-surface rounded-xl shadow-2xl max-w-sm w-full p-6 border border-outline-variant/30"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="wss-dialog-title" className="font-headline font-bold text-lg text-secondary">
          {active.title}
        </h3>
        <p className="text-sm text-outline mt-2 leading-relaxed whitespace-pre-line">
          {active.message}
        </p>

        {active.kind === 'prompt' && (
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={active.placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirm();
              }
            }}
            className="w-full mt-4 border border-outline-variant rounded-lg p-3 text-sm focus:border-primary outline-none"
          />
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={cancel} className={cx(BUTTON_BASE, BUTTON_CANCEL)}>
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={confirm}
            disabled={disableConfirm}
            className={cx(
              BUTTON_BASE,
              tone === 'destructive' ? BUTTON_CONFIRM_DESTRUCTIVE : BUTTON_CONFIRM_DEFAULT,
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
